import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChildren } from '@angular/core';
import { NgFor } from '@angular/common';

/**
 * Presentational 6-digit OTP entry — a grid of single-character cells with a mid
 * separator, auto-advance, backspace-to-previous, arrow navigation, full-code paste,
 * and error/success visual states. Dialog-agnostic: it holds no verification logic
 * and emits the entered code for a host to act on. Extracted from the 2FA
 * confirmation dialog so the login challenge and the enrollment stepper share one
 * control (SD-8102).
 */
@Component({
    selector: 'app-otp-code-input',
    templateUrl: './otp-code-input.component.html',
    styleUrls: ['./otp-code-input.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgFor],
})
export class OtpCodeInputComponent implements OnDestroy
{
    /** Blocks input (e.g. while the host is verifying or has succeeded). */
    @Input() disabled = false;
    /** Renders the error state (red cells + shake). Host toggles it, then calls reset(). */
    @Input() error = false;
    /** Renders the success state (green cells). */
    @Input() success = false;

    /** Emits the current code on every change (partial or full). */
    @Output() valueChange = new EventEmitter<string>();
    /** Emits the full code once all six digits are entered (auto-advance or paste). */
    @Output() completed = new EventEmitter<string>();

    @ViewChildren('digitInput') private _digitInputs: QueryList<ElementRef<HTMLInputElement>>;

    /** The two cell groups either side of the separator (indices 0-2 and 3-5). */
    readonly firstGroup = [0, 1, 2];
    readonly secondGroup = [3, 4, 5];

    digits: string[] = ['', '', '', '', '', ''];

    private _pasteTimer: ReturnType<typeof setTimeout> | null = null;

    get filledCount(): number {
        return this.digits.filter(d => d !== '').length;
    }

    /** Stable identity for the (static) cell indices. */
    trackByDigit(_: number, digit: number): number {
        return digit;
    }

    ngOnDestroy(): void {
        if (this._pasteTimer) {
            clearTimeout(this._pasteTimer);
            this._pasteTimer = null;
        }
    }

    onDigitInput(event: Event, index: number): void {
        const input = event.target as HTMLInputElement;
        const value = input.value;

        // Only allow single digits — keep the last char if more than one arrived.
        if (value.length > 1) {
            input.value = value.charAt(value.length - 1);
        }

        if (!/^\d$/.test(input.value)) {
            input.value = '';
            this.digits[index] = '';
            this.valueChange.emit(this.code);
            return;
        }

        this.digits[index] = input.value;
        this.valueChange.emit(this.code);

        if (index < 5) {
            this.focusDigit(index + 1);
        }

        if (this.digits.every(d => d !== '')) {
            this.completed.emit(this.code);
        }
    }

    onDigitKeydown(event: KeyboardEvent, index: number): void {
        if (event.key === 'Backspace') {
            if (this.digits[index] === '' && index > 0) {
                this.digits[index - 1] = '';
                this.valueChange.emit(this.code);
                this.focusDigit(index - 1);
                event.preventDefault();
            } else {
                this.digits[index] = '';
                this.valueChange.emit(this.code);
            }
        } else if (event.key === 'ArrowLeft' && index > 0) {
            this.focusDigit(index - 1);
            event.preventDefault();
        } else if (event.key === 'ArrowRight' && index < 5) {
            this.focusDigit(index + 1);
            event.preventDefault();
        }
    }

    onDigitPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const pasted = event.clipboardData?.getData('text')?.replace(/\D/g, '').slice(0, 6);
        if (!pasted) {
            return;
        }

        for (let i = 0; i < 6; i++) {
            this.digits[i] = pasted[i] || '';
        }
        this.valueChange.emit(this.code);

        // Defer to let the [value] bindings flush, then advance/submit. Tracked so it
        // can't fire after the component is destroyed.
        if (this._pasteTimer) {
            clearTimeout(this._pasteTimer);
        }
        this._pasteTimer = setTimeout(() => {
            this._pasteTimer = null;
            const inputs = this._digitInputs.toArray();
            this.digits.forEach((d, i) => {
                if (inputs[i]) {
                    inputs[i].nativeElement.value = d;
                }
            });

            if (pasted.length >= 6) {
                this.completed.emit(this.code);
            } else {
                this.focusDigit(pasted.length);
            }
        });
    }

    /** Clear all cells and refocus the first — the host calls this after an error flash. */
    reset(): void {
        this.digits = ['', '', '', '', '', ''];
        const inputs = this._digitInputs?.toArray();
        inputs?.forEach(input => input.nativeElement.value = '');
        this.valueChange.emit(this.code);
        this.focusDigit(0);
    }

    /** Focus the first cell (host calls this when the input becomes active). */
    focusFirst(): void {
        this.focusDigit(0);
    }

    private get code(): string {
        return this.digits.join('');
    }

    private focusDigit(index: number): void {
        const inputs = this._digitInputs?.toArray();
        if (inputs && inputs[index]) {
            inputs[index].nativeElement.focus();
            inputs[index].nativeElement.select();
        }
    }
}
